// ============================================================================
// src/app/data/routeResourceQuery.ts — route resource -> useQuery options
// ============================================================================
// B3.5. The pure half of the resource runtime: given a catalog resource, a
// scope and params, produce the exact options object `useQuery` is called with.
//
// It is a separate module for one reason, and it is not tidiness. This repo's
// vitest environment is `node` with no renderer, so a hook's body cannot be
// mounted and cannot be tested — a documented gap, and the reason the interim
// runtime's regression guards had to be written as assertions about SOURCE TEXT
// rather than about behaviour. Everything in this file is reachable from a
// plain `node` test:
//
//   * which key a query runs under, and that a query with no identity gets the
//     inert one rather than an invented one;
//   * whether it may run at all;
//   * that the query function is handed (client, params, signal) in that order,
//     and that a throw from the resource propagates rather than being swallowed.
//
// That is where the correctness lives. What is left in the hook is
// subscription plumbing, and Query owns that.
// ============================================================================

import {skipToken} from '@tanstack/react-query';

import {UNRESOLVED_RESOURCE_KEY} from './routeDataCatalog.js';
import type {OneShotRouteResource, QueryScope, ResourceParams} from './routeDataCatalog.js';
// The client type comes from src/lib/supabaseTypes.ts, NOT from the library.
// tests/static/supabase_client_owner_static.test.js asserts the repository
// contains exactly one textual occurrence of the '@supabase/supabase-js'
// specifier, and it counts TEXT — an erased `import type` would still break it.
import type {SupabaseClient} from '../../lib/supabaseTypes.js';

/** What a caller may decide. Everything about caching belongs to queryClient. */
export type RouteResourceQueryInput<Data> = {
  resource: OneShotRouteResource<Data>;
  scope: QueryScope | null;
  params?: ResourceParams;
  enabled?: boolean;
  client: SupabaseClient;
};

/**
 * The three fields handed to `useQuery`.
 *
 * `queryKey` is widened to `readonly unknown[]` because the two branches return
 * genuinely different shapes — a built `QueryKey` tuple, or the frozen inert
 * key — and narrowing it would force a cast at the one place the distinction
 * actually matters.
 */
export type RouteResourceQueryOptions<Data> = {
  queryKey: readonly unknown[];
  queryFn: typeof skipToken | ((context: {signal: AbortSignal}) => Promise<Data>);
  enabled: boolean;
};

/**
 * The structural subset of a Query result this projection reads. Deliberately
 * not the library's `QueryObserverResult`: the test drives this with results
 * from a REAL QueryObserver, and a narrower hand-written shape keeps that free
 * to pass whatever the observer actually produces.
 */
export type RouteResourceQueryLike<Data> = {
  readonly data: Data | undefined;
  readonly isPending: boolean;
  readonly isFetching: boolean;
  readonly error: unknown;
};

/** The {data, loading, error} contract every surface consumes. */
export type RouteResourceProjection<Data> = {
  data: Data | null;
  loading: boolean;
  error: unknown;
};

/**
 * Build the useQuery options for one route resource.
 *
 * Deliberately returns ONLY the three fields a call site is allowed to decide.
 * Everything about caching and refetching — staleTime, gcTime, retry, the
 * refetch-on-* triggers, networkMode — is the QueryClient's, set once in
 * queryClient.js with a written reason per option. A per-call override here
 * would be a second, unreviewed place where this app starts caching, which is
 * the failure the single-owner rule exists to prevent.
 */
export function routeResourceQueryOptions<Data>({
  resource,
  scope,
  params,
  enabled = true,
  client,
}: RouteResourceQueryInput<Data>): RouteResourceQueryOptions<Data> {
  if (
    !resource ||
    resource.kind !== 'one-shot' ||
    typeof resource.query !== 'function' ||
    typeof resource.key !== 'function'
  ) {
    throw new Error('routeResourceQueryOptions: a one-shot catalog resource with key() and query() is required');
  }

  // No scope means identity has not settled. That is not an error and not an
  // empty result — it is "not yet", and the only correct behaviour is to run
  // nothing. The observer parks on the inert key from the reviewed builder;
  // `skipToken` makes the absence of a runnable function explicit to Query
  // rather than leaving a live function behind a flag someone could flip.
  if (!scope) {
    return {
      queryKey: UNRESOLVED_RESOURCE_KEY,
      queryFn: skipToken,
      enabled: false,
    };
  }

  return {
    // The key the whole cache identity rests on. Built by the resource, which
    // delegates to resourceKey, which folds in the full subject scope — so an
    // entry can never be read back under a different user, a different
    // effective role, or a different role preview.
    queryKey: resource.key(scope, params),

    // Called with (client, params, signal), which is the arity the catalog's
    // pure query functions already have and the reason they were written that
    // way in Prerequisite A.
    //
    // DESTRUCTURING `signal` HERE IS LOAD-BEARING, NOT STYLE. Query decides
    // whether to abort a superseded request by whether the query function ever
    // READ context.signal — the property is a getter that records the access.
    // If it did, losing the last observer (an unmount, or a key change) cancels
    // the request outright; if it did not, Query lets it run to completion and
    // throws the result away. Verified both ways against 5.101.4.
    //
    // So a rewrite that stops reading `signal` — passing `undefined`, or taking
    // the whole context object and forwarding a field off it later — would
    // silently drop the cancellation the interim runtime was built to add,
    // leaving seven requests per animal running for every animal an operator
    // skips past. There is a test on exactly this.
    //
    // `params` comes from the closure rather than from the key. The key holds
    // NORMALIZED params (sorted, undefined stripped) and, for the record
    // resource, only the subset that defines identity — so reconstructing the
    // call arguments from it would silently drop anything the key does not
    // carry. Passing the caller's object is what today's runtime does. The
    // hazard it inherits is the same one: a param that is NOT in the key can
    // change without changing identity. That is a property of the resource's
    // key function, and the place to fix it is the key function.
    queryFn: ({signal}) => resource.query(client, params, signal),

    // `enabled` gates FETCHING, not identity: a caller-disabled query keeps its
    // real key and its real function, so re-enabling it needs no re-keying.
    enabled: Boolean(enabled),
  };
}

/**
 * Project a Query result onto the {data, loading, error} contract the surfaces
 * consume.
 *
 * Pure, and separate from the hook for the same reason as the options builder:
 * this repo's vitest environment has no renderer, so anything left inside a
 * hook body is untestable. Here it can be driven with REAL results from a real
 * QueryObserver, which is how the rules below are proved rather than asserted
 * about source text.
 *
 * `loading` means "there is nothing to show and something is happening", NOT
 * "a request is in flight". That distinction is the whole fix:
 *
 *   not ready             -> true. Identity has not settled. A record page must
 *                            never render "not found" over an unresolved user;
 *                            neither may it render a not-loading/not-found frame
 *                            for one paint on the way there.
 *   first load, key change-> true via isPending. There is no data for this key.
 *   retry after an error  -> true via the third clause. isPending is FALSE in an
 *                            error state, so without it a Retry press would
 *                            produce no spinner and read as a dead button.
 *   same-key refetch      -> FALSE. Data is on screen and stays there while it
 *                            refreshes underneath. This is the clause that stops
 *                            the cattle record page unmounting its editor — and
 *                            eating whatever the operator was typing — after
 *                            every single field save.
 */
export function routeResourceResult<Data>(
  query: RouteResourceQueryLike<Data>,
  ready: boolean,
): RouteResourceProjection<Data> {
  // A disabled resource reports exactly what an unresolved one does. Anything
  // it loaded before it was disabled must not stay on screen, and "disabled" is
  // never an error.
  if (!ready) return {data: null, loading: true, error: null};
  return {
    // Query reports absence as undefined; this contract has always used null.
    data: query.data === undefined ? null : query.data,
    loading: Boolean(query.isPending || (query.isFetching && query.data === undefined)),
    error: query.error ?? null,
  };
}

// ---------------------------------------------------------------------------
// A NOTE ON CANCELLATION, BECAUSE THE OBVIOUS ASSUMPTION IS WRONG BOTH WAYS
//
// The first draft of this module carried a hand-written cancelOrphanedQuery
// helper and an effect to call it, on the belief that Query lets a superseded
// request run to completion. It does not: when a query loses its last observer
// — an unmount, or a key change — Query cancels the in-flight request, and the
// AbortSignal handed to the query function fires. Measured, then the helper was
// deleted rather than shipped as a no-op nobody could see was redundant.
//
// The catch is the CONDITION, which is not documented anywhere near the option
// it depends on: Query only cancels if the query function actually READ
// `context.signal`. If it never touched it, the request is left to finish and
// its result discarded. That is why the wrapper above destructures `signal` in
// its parameter list, and why the abort has a test of its own.
// ---------------------------------------------------------------------------
